import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    Injector,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
    inject,
} from '@angular/core';
import {
    AbstractControl,
    FormGroupDirective,
    NgControl,
    UntypedFormGroup,
} from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { BaseWdxFormControlClass } from '../../../abstract-classes/base-form-control.class';

import { delay, takeUntil } from 'rxjs/operators';
import {
    IFormDynamicData,
    IWrappedFormComponent,
    IWrappedFormComponentProvider,
} from '../../../interfaces';
import { ValidationErrorMessagePipe } from '../../../pipes';
import { FormWrappedControlDirective } from './form-wrapped-control-host.directive';
import { FormConditionsService } from '../../../services';
import { ReactiveFormLayoutAndDefinition } from '../../../models';

@Component({
    selector: 'wdx-ff-form-wrapped-control',
    templateUrl: './form-wrapped-control.component.html',
    standalone: false,
})
export class FormWrappedControlComponent
    extends BaseWdxFormControlClass
    implements OnInit, OnChanges
{
    @Input() formContext!: any;
    @Input() entityId!: string;
    @ViewChild(FormWrappedControlDirective, { static: true })
    formWrappedControlHost!: FormWrappedControlDirective;
    public validationErrorMessage = '';

    private validationErrorMessagePipe = new ValidationErrorMessagePipe();
    private formControlService = inject(IWrappedFormComponentProvider);
    private formConditionsService = inject(FormConditionsService);
    private injector = inject(Injector);
    private cdr = inject(ChangeDetectorRef);
    private componentRef!: ComponentRef<IWrappedFormComponent>;
    private ngControl!: NgControl;

    formData$ = new BehaviorSubject<UntypedFormGroup>(null as any);

    constructor(
        public override controlContainer: FormGroupDirective,
        public override dynamicDataService: IFormDynamicData,
    ) {
        super(controlContainer, dynamicDataService);
        this.controlContainer.form.valueChanges
            // need a delay to ensure this is done after any form conditions/triggers have run
            .pipe(takeUntil(this.destroyed$), delay(0))
            .subscribe((_) => {
                this.formData$.next(this.controlContainer?.form.getRawValue());
                this.updateValidationErrorMessage();
            });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['formElement'].currentValue) {
            const formElement = changes['formElement']
                .currentValue as ReactiveFormLayoutAndDefinition;
            if (changes['formElement'].firstChange) {
                return;
            }
            this.componentRef?.setInput('formInputData', formElement);
        }
    }

    ngOnInit(): void {
        this.ngControl = this.injector.get(NgControl);

        if (this.formElement?.contextualValues?.length) {
            this.formData$.next(this.controlContainer?.form.getRawValue());
        }
        this.formConditionsService.updateControlIsDisabled(
            this.formControl as AbstractControl,
            this.isDisabled as boolean,
            'wrappedFormControl',
        );

        this.formData$
            .pipe(takeUntil(this.destroyed$))
            .subscribe((formData) => {
                this.formControlService
                    .getComponent$(
                        this.formElement,
                        this.formContext,
                        formData,
                        this.entityId,
                        this.formControl,
                    )
                    .subscribe(({ component, inputs }) => {
                        if (!this.componentRef) {
                            const viewContainerRef =
                                this.formWrappedControlHost.viewContainerRef;
                            this.componentRef =
                                viewContainerRef.createComponent(component);

                            this.ngControl.valueAccessor =
                                this.componentRef.instance;

                            this.componentRef.instance?.blurred
                                ?.pipe(takeUntil(this.destroyed$), delay(0))
                                .subscribe(() => {
                                    this.ngControl.control?.markAsTouched();
                                    this.updateValidationErrorMessage();
                                });
                        }

                        Object.entries(inputs).forEach(([key, value]) => {
                            this.componentRef.instance[key] = value;
                        });
                        this.cdr.markForCheck();
                    });
            });
    }

    private updateValidationErrorMessage() {
        if (
            !this.formControlService.hideErrorMessages &&
            this.ngControl &&
            this.ngControl.control?.touched
        ) {
            this.validationErrorMessage =
                this.validationErrorMessagePipe.transform(
                    this.ngControl.control.errors,
                );
            if (this.componentRef) {
                this.componentRef.setInput(
                    'validationErrorMessage',
                    this.validationErrorMessage,
                );
            }
        }
    }
}
